import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Box} from '@primer/styled-react'
import {useMemo} from 'react'

import {VALUES} from '../../constants/values'
import {TimelineDivider} from './TimelineDivider'
import styles from './TimelineRowBorder.module.css'

export type TimelineRowBorderProps = {
  children: React.ReactNode
  addDivider: boolean
  item: {
    __id: string
  }
  isMajor?: boolean
  isHighlighted?: boolean
  sx?: BetterSystemStyleObject
  commentParams?: TimelineRowBorderCommentParams
  ref?: React.Ref<HTMLDivElement>
}

export type TimelineRowBorderCommentParams = {
  first: boolean
  last: boolean
  viewerDidAuthor?: boolean
}

const sharedStyling: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  borderRadius: 2,
  backgroundColor: 'canvas.default',
  transition: '0.2s',
}

export const TimelineRowBorder = ({ref, ...props}: TimelineRowBorderProps) => {
  const {children, addDivider, item, isMajor, isHighlighted, commentParams, sx} = props

  const highlightedStyling = useMemo(
    () =>
      isHighlighted
        ? {
            border: '1px solid',
            borderColor: 'accent.fg',
            boxShadow: `0px 0px 0px 1px var(--fgColor-accent, var(--color-accent-fg))`,
          }
        : {},
    [isHighlighted],
  )

  const majorEventStyling = useMemo(() => {
    return {
      ...sharedStyling,
      border: '1px solid',
      borderColor: commentParams?.viewerDidAuthor ? 'accent.muted' : 'border.default',
      py: 0,
      ...highlightedStyling,
      ...sx,
      paddingTop: '0px',
    }
  }, [commentParams?.viewerDidAuthor, highlightedStyling, sx])

  const defaultStyling = useMemo(() => {
    return {
      pl: '12px',
      ...sharedStyling,
      ...highlightedStyling,
      ...sx,
    }
  }, [highlightedStyling, sx])

  const dataProps = {
    [VALUES.timeline.dataTimelineEventId]: item.__id,
    'data-highlighted-event': isHighlighted,
  }
  return (
    <div className={styles.Box}>
      {addDivider && <TimelineDivider id={item.__id} />}
      <Box
        {...dataProps}
        ref={ref}
        key={item.__id}
        sx={isMajor ? majorEventStyling : defaultStyling}
        data-testid={`timeline-row-border-${item.__id}`}
      >
        {children}
      </Box>
    </div>
  )
}
TimelineRowBorder.displayName = 'TimelineRowBorder'
